<script setup lang="ts">
import { getSkillName } from '@/core/skill'
import { useUserStore } from '@/stores/modules/user'
import type { SkillItemType } from '@/types/skill'
import { socketEmitAsync } from '@/utils/web-socket'
import SkillInfo from '@/views/item/components/SkillInfo.vue'
import { QuestionFilled } from '@element-plus/icons-vue'
import { onBeforeUnmount, ref } from 'vue'

const userStore = useUserStore()

const receiveShow = ref(false)
const receiveSkillList = ref<SkillItemType[]>()

async function handleBless(count: number = 1) {
  if (userStore.consumables.skillEnlightenmentScroll < count) {
    return ElMessage.warning('启迪卷轴不足')
  }
  if (userStore.skillList.length + count > userStore.getSkillCapacity) {
    return ElMessage.warning('技能背包已满')
  }

  try {
    await ElMessageBox.confirm(`<p class="affix">确定进行启迪吗？（本次启迪花费 ${count}<span class="mp"> 启迪卷轴</span>）</p>`, '提示', {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    })

    const res = await socketEmitAsync<{ skillList: SkillItemType[]; skillBlessTimes: number }>({ type: 'skillBless', data: { count } })
    const { skillList, skillBlessTimes } = res

    userStore.skillList.push(...skillList)
    userStore.consumables.skillEnlightenmentScroll -= count
    userStore.consumables.antiquitySkillPageFragment += count
    userStore.skillBlessTimes = skillBlessTimes

    receiveSkillList.value = skillList
    receiveShow.value = true
  } catch {
    return
  }
}

onBeforeUnmount(() => {
  ElMessageBox.close()
  receiveShow.value = false
})
</script>

<template>
  <div class="skill-bless">
    <div class="resource-list affix">
      <span>🧾 {{ userStore.consumables.skillEnlightenmentScroll }}</span>
      <span>🔸 {{ userStore.consumables.antiquitySkillPageFragment }}</span>
    </div>

    <div class="bless-wrap border-wrap">
      <h4 class="affix">
        <span class="mp">技能启迪</span>
        <div class="help-info">
          <el-popover placement="bottom" width="100%" trigger="click">
            <template #reference>
              <el-icon size="16"><QuestionFilled /></el-icon>
            </template>
            <div class="affix">
              <h4>启迪说明</h4>
              <p>
                1. 消耗<span class="mp">启迪卷轴</span>可获得 <span class="grow">1</span> 个<span class="antiquity">秘技残页</span>，并随机获取<span
                  class="mp"
                  >技能书</span
                >。
              </p>
              <p>
                2. 保底时必定获得<span class="legend">传说</span>技能。启迪概率公示：<span class="normal">普通</span> 50%，<span class="race"
                  >稀有</span
                >
                30%，<span class="epic">史诗</span> 15%，<span class="legend">传说</span> 5%。
              </p>
            </div>
          </el-popover>
        </div>
      </h4>

      <div class="bless-list">
        <div class="bless-item">
          <p>1 启迪卷轴 / 次</p>
          <p>距离保底次数：{{ 10 - userStore.skillBlessTimes }}</p>
          <el-button type="primary" plain @click="handleBless(1)">启迪 1次</el-button>
          <el-button type="primary" plain @click="handleBless(10)">启迪 10次</el-button>
        </div>
      </div>
    </div>

    <el-dialog v-model="receiveShow" width="100%" style="max-width: 500px" destroy-on-close>
      <h2>恭喜获得</h2>

      <div class="btn-wrap" style="max-height: 50vh; overflow: auto">
        <el-row :gutter="10">
          <el-col :span="12" v-for="item of receiveSkillList" :key="item.id">
            <el-popover placement="bottom" width="300px" trigger="click">
              <template #reference>
                <CommonBtn size="small" style="margin-bottom: 5px">
                  <span :class="item.quality" v-html="getSkillName(item)"></span>
                </CommonBtn>
              </template>
              <SkillInfo :skill-info="item" :show-action="false" />
            </el-popover>
          </el-col>
        </el-row>
      </div>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="receiveShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.skill-bless {
  margin-bottom: 10px;
  flex-flow: column nowrap;
  h4 {
    display: flex;
    justify-content: center;
    position: relative;
    .help-info {
      position: absolute;
      margin-left: 10px;
      right: 30%;
    }
  }
  .resource-list {
    text-align: right;
    font-size: 14px;
    margin: 5px 0;
    span {
      margin-left: 10px;
    }
  }
  .bless-list {
    display: flex;
    justify-content: space-between;
    .bless-item {
      margin: 0 auto;
      display: flex;
      flex-flow: column nowrap;
      width: 60%;
      align-items: center;
      h5 {
        font-size: 15px;
        margin: 10px 0;
      }
      p {
        font-size: 14px;
        margin: 5px 0;
        text-align: center;
      }
      .el-button {
        margin-left: 0;
        margin-bottom: 5px;
      }
    }
  }
  .btn-wrap > div {
    align-items: flex-start;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
}
</style>
